'use client';

import { motion } from 'framer-motion';
import { useState } from 'react';

import { Box, cn, Image } from '@zoomit/primitives';
import type { MenuItemType } from '@zoomit/web-kit';
import { BaseMediaUrl, IconRenderer } from '@zoomit/web-kit';
import { cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { canDisplayMenuItem } from '../../../helpers/menu/canDisplayMenuItem';
import { spreadIntoColumns } from '../../../helpers/menu/spreadIntoColumns';

type Props = {
  items?: MenuItemType[];
  triggeredItem?: MenuItemType;
};

const DefaultMenuSubItemColumnDesktop = (props: Props) => {
  const columns = spreadIntoColumns(props.items || []);
  const [triggeredSubItem, setTriggeredSubItem] = useState<MenuItemType>();
  const getIsActive = (title: string) => {
    if (!triggeredSubItem?.children) return false;
    if (triggeredSubItem.children.length === 0) return false;

    return triggeredSubItem?.title === title;
  };

  const handleMenuItemHover = (menuItem: MenuItemType, index: number) => {
    setTriggeredSubItem(menuItem);
  };

  return (
    <motion.div
      key={`${props.triggeredItem?.title}-${props.items?.length}-subitems-${props.items?.map((e) => e.title).join(',')}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex"
    >
      {columns.map((columnItems, index) => (
        <motion.div className={'m-6 mr-0 flex w-max min-w-[140px] flex-col gap-2 pr-6'} key={index}>
          {columnItems.map((item, index) => {
            const SubItemComponent = item.url ? 'a' : 'div';

            return (
              <>
                {canDisplayMenuItem.renderImage(item.displayType) && item.image && (
                  <Box
                    style={{
                      aspectRatio: cssVar(theme.aspectRatio.primary),
                      position: 'relative',
                      width: 200,
                    }}
                  >
                    <Image
                      style={{
                        objectFit: 'cover',
                        borderRadius: cssVar(theme.radius.sm),
                      }}
                      src={`${BaseMediaUrl}/${item.image.id}`}
                      alt={item.image.alt}
                      fill
                    />
                  </Box>
                )}
                <SubItemComponent
                  onPointerEnter={() => handleMenuItemHover(item, index)}
                  key={index}
                  href={item.url || ''}
                  className={cn(
                    'text-body before:bg-primary1 hover:text-primary2 hover:[&_svg]:fill-primary2 flex w-fit items-center gap-2 leading-7 before:h-4 before:w-1 before:content-[""]',
                    getIsActive(item.title) ? 'text-primary2' : 'text-text',
                    item.isHighlighted ? 'font-black' : 'font-normal',
                    item.isHighlighted && !item.icon ? 'before:inline-block' : 'before:hidden',
                    !item.url && 'cursor-default',
                  )}
                >
                  {canDisplayMenuItem.renderIcon(item.displayType) && item.icon?.content && (
                    <IconRenderer icon={item.icon.content} fill={cssVar(theme.grey1)} />
                  )}
                  <Typography
                    className={
                      'lg:whitespace-[unset] lg:overflow-[unset] line-clamp-1 w-[130px] text-current lg:w-[unset] lg:[text-overflow:unset]'
                    }
                  >
                    {item.title}
                  </Typography>
                </SubItemComponent>
              </>
            );
          })}
        </motion.div>
      ))}
      {(triggeredSubItem?.children.length || 0) > 0 && (
        <DefaultMenuSubItemColumnDesktop
          key={`${props.triggeredItem?.title}-${props.items?.length}`}
          items={triggeredSubItem?.children}
          triggeredItem={triggeredSubItem}
        />
      )}
    </motion.div>
  );
};

export default DefaultMenuSubItemColumnDesktop;
